const FIELD_PATTERN = /^[A-Za-z_][A-Za-z0-9_.]*$/;

const snakeCaseKey = (key) => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const isPlainNumber = (value) => /^-?\d+(\.\d+)?$/.test(String(value));

const escapeFacetValue = (value) => {
	if (value === 'true' || value === 'false' || isPlainNumber(value)) return value;
	return `\`${String(value).replaceAll('`', '')}\``;
};

const splitIndexName = (indexName) => {
	const [collection, sortBy] = indexName.split('/sort/');
	return { collection, sortBy };
};

const parseFacetFilter = (facetFilter) => {
	const colon = facetFilter.indexOf(':');
	if (colon <= 0) return null;
	return {
		fieldName: facetFilter.slice(0, colon),
		fieldValue: facetFilter.slice(colon + 1),
	};
};

const facetClause = (fieldName, values) => {
	const excluded = [];
	const included = [];
	for (const value of values) {
		if (typeof value === 'string' && value.startsWith('-') && !isPlainNumber(value)) {
			excluded.push(value.slice(1));
		} else {
			included.push(value);
		}
	}

	const parts = [];
	if (included.length > 0) {
		parts.push(`${fieldName}:=[${included.map(escapeFacetValue).join(',')}]`);
	}
	if (excluded.length > 0) {
		parts.push(`${fieldName}:!=[${excluded.map(escapeFacetValue).join(',')}]`);
	}
	return parts.join(' && ');
};

const adaptFacetFilters = (facetFilters) => {
	if (!facetFilters) return '';
	const items = Array.isArray(facetFilters) ? facetFilters : [facetFilters];
	return items
		.map((item) => {
			const filters = Array.isArray(item) ? item : [item];
			const byField = new Map();
			for (const filter of filters) {
				if (typeof filter !== 'string') continue;
				const parsed = parseFacetFilter(filter);
				if (!parsed) continue;
				const values = byField.get(parsed.fieldName) ?? [];
				values.push(parsed.fieldValue);
				byField.set(parsed.fieldName, values);
			}
			return [...byField.entries()]
				.map(([field, values]) => facetClause(field, values))
				.join(' && ');
		})
		.filter(Boolean)
		.join(' && ');
};

const NUMERIC_FILTER = /^(.*?)(<=|>=|>|<|=)(.*)$/;

const adaptNumericFilters = (numericFilters) => {
	if (!Array.isArray(numericFilters)) return '';
	const grouped = new Map();
	for (const filter of numericFilters) {
		if (typeof filter !== 'string') continue;
		const match = NUMERIC_FILTER.exec(filter);
		if (!match) continue;
		const [, fieldName, operator, fieldValue] = match;
		const operators = grouped.get(fieldName) ?? {};
		operators[operator] = fieldValue;
		grouped.set(fieldName, operators);
	}

	const clauses = [];
	for (const [field, operators] of grouped) {
		if (operators['<='] != null && operators['>='] != null) {
			clauses.push(`${field}:=[${operators['>=']}..${operators['<=']}]`);
		} else if (operators['<='] != null) {
			clauses.push(`${field}:<=${operators['<=']}`);
		} else if (operators['>='] != null) {
			clauses.push(`${field}:>=${operators['>=']}`);
		} else if (operators['>'] != null) {
			clauses.push(`${field}:>${operators['>']}`);
		} else if (operators['<'] != null) {
			clauses.push(`${field}:<${operators['<']}`);
		} else if (operators['='] != null) {
			clauses.push(`${field}:=${operators['=']}`);
		}
	}
	return clauses.join(' && ');
};

const adaptFilters = (params) =>
	[
		params.filters,
		adaptFacetFilters(params.facetFilters),
		adaptNumericFilters(params.numericFilters),
	]
		.filter((filter) => filter)
		.join(' && ');

const adaptFacetBy = (facets) => {
	if (!facets) return '';
	const names = (Array.isArray(facets) ? facets : [facets]).filter(
		(facet) => typeof facet === 'string' && FIELD_PATTERN.test(facet),
	);
	return names.join(',');
};

const replaceHighlightTags = (value, highlightPreTag, highlightPostTag) =>
	String(value)
		.replaceAll('<mark>', highlightPreTag || '<mark>')
		.replaceAll('</mark>', highlightPostTag || '</mark>');

const adaptHighlight = (document, highlights, params, snippetOrValue) => {
	const byField = new Map((highlights ?? []).map((item) => [item.field, item]));
	const result = {};
	for (const [attribute, value] of Object.entries(document)) {
		if (value != null && typeof value === 'object') continue;
		const highlight = byField.get(attribute);
		const raw = highlight?.[snippetOrValue] ?? highlight?.value ?? value ?? '';
		result[attribute] = {
			value: replaceHighlightTags(raw, params.highlightPreTag, params.highlightPostTag),
			matchLevel: highlight ? 'full' : 'none',
			matchedWords: highlight?.matched_tokens ?? [],
		};
	}
	return result;
};

const adaptFacets = (facetCounts = []) => {
	const facets = {};
	for (const facet of facetCounts) {
		const counts = {};
		for (const count of facet.counts ?? []) {
			counts[count.value] = count.count;
		}
		facets[facet.field_name] = counts;
	}
	return facets;
};

const adaptFacetStats = (facetCounts = []) => {
	const stats = {};
	for (const facet of facetCounts) {
		if (facet.stats && Object.keys(facet.stats).length > 0) {
			stats[facet.field_name] = facet.stats;
		}
	}
	return stats;
};

const numberOfPages = (found, perPage) => {
	if (!perPage) return 0;
	const pages = found / perPage;
	return Number.isFinite(pages) ? Math.ceil(pages) : 0;
};

/**
 * Turns one InstantSearch request into one Typesense multi_search entry.
 * Same split the typesense-instantsearch-adapter uses: index name, filters,
 * facets and page on the way in. The Folio proxy, not the browser, talks to Typesense.
 */
const buildTypesenseSearch = (request, additionalSearchParameters = {}) => {
	const { collection, sortBy } = splitIndexName(request.indexName);
	const params = request.params ?? {};
	const search = {
		collection,
		q: params.query === '' || params.query == null ? '*' : params.query,
		page: (params.page || 0) + 1,
	};

	for (const [key, value] of Object.entries(additionalSearchParameters)) {
		if (value != null && value !== '') {
			search[snakeCaseKey(key)] = value;
		}
	}

	const filterBy = adaptFilters(params);
	if (filterBy) search.filter_by = filterBy;
	if (sortBy) search.sort_by = sortBy;
	if (params.hitsPerPage != null) search.per_page = params.hitsPerPage;

	const facetBy = adaptFacetBy(params.facets);
	if (facetBy) search.facet_by = facetBy;
	if (params.maxValuesPerFacet != null) search.max_facet_values = params.maxValuesPerFacet;

	if (params.facetQuery) {
		search.facet_query = `${params.facetName}:${params.facetQuery}`;
		search.per_page = 0;
	}

	return search;
};

const adaptSearchResponse = (typesenseResult, request) => {
	if (typesenseResult?.error || !typesenseResult?.hits) {
		throw new Error(typesenseResult?.error || 'Search failed');
	}

	const params = request.params ?? {};
	const perPage = typesenseResult.request_params?.per_page ?? params.hitsPerPage;
	return {
		hits: typesenseResult.hits.map((typesenseHit) => {
			const document = typesenseHit.document;
			return {
				...document,
				objectID: document.id,
				_highlightResult: adaptHighlight(
					document,
					typesenseHit.highlights,
					params,
					'value',
				),
				_snippetResult: adaptHighlight(
					document,
					typesenseHit.highlights,
					params,
					'snippet',
				),
			};
		}),
		nbHits: typesenseResult.found,
		page: typesenseResult.page - 1,
		nbPages: numberOfPages(typesenseResult.found, perPage),
		hitsPerPage: perPage,
		facets: adaptFacets(typesenseResult.facet_counts),
		facets_stats: adaptFacetStats(typesenseResult.facet_counts),
		query: typesenseResult.request_params?.q,
		processingTimeMS: typesenseResult.search_time_ms,
	};
};

const adaptFacetSearchResponse = (typesenseResult, request) => {
	if (typesenseResult?.error) {
		throw new Error(typesenseResult.error);
	}

	const params = request.params ?? {};
	const facet = (typesenseResult.facet_counts ?? []).find(
		(item) => item.field_name === params.facetName,
	);
	return {
		facetHits: (facet?.counts ?? []).map((count) => ({
			value: count.value,
			highlighted: replaceHighlightTags(
				count.highlighted,
				params.highlightPreTag,
				params.highlightPostTag,
			),
			count: count.count,
		})),
		exhaustiveFacetsCount: true,
		processingTimeMS: typesenseResult.search_time_ms,
	};
};

export { adaptFacetSearchResponse, adaptSearchResponse, buildTypesenseSearch };
